"use client";
import React, { useCallback, useMemo, useState } from "react";

import clsx from "clsx";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";

import { useAuth } from "@/components/domains/auth";
import { Modal } from "@/components/feedbacks";
import { Button } from "@/components/inputs";
import { defaultToastClassNames } from "@/components/providers/ToastProvider";
import { useSubscriptionStatus } from "@/features";
import { ProductModel } from "@/models/product/type";
import { usePostParsedCartFromCartItems } from "@/queries";
import { useClientFrontStoreCartItems, useClientFrontStoreSetNames } from "@/storage";
import { numberFormat, withCsr } from "@/utils";
import { sumBy } from "@/utils/array";

import { CartAddedNotification } from "./CartAddedNotification";
import { useAddToCart, useHandleScroll } from "./helpers";
import { usePrices } from "./hooks/usePrices";
import useRedirectToSubscription from "./hooks/useRedirectToSubscription";
import { MobileFloatPurchaseBars } from "./MobileFloatPurchaseBars";
import styles from "./ProductDetailSelects.module.scss";
import { ProductDetailSelectsNormal } from "./ProductDetailSelectsNormal";
import { ProductDetailSelectsOutlet } from "./ProductDetailSelectsOutlet";
import { ProductDetailSelectsSet } from "./ProductDetailSelectsSet";

interface ProductDetailSelectsProps {
  isMobile: boolean;
  product: ProductModel;
  defaultQuantity: number;
}

/**
 * 認証情報で分岐のロジック、useClientFrontStoreCartItemsがあるのでwithCsrでラップ
 */
export const ProductDetailSelects = withCsr(function ProductDetailSelects({
  isMobile = false,
  product,
  defaultQuantity,
}: ProductDetailSelectsProps): React.ReactNode {
  const router = useRouter();
  const { isScrollLimit, showCTAButton } = useHandleScroll();
  const [quantity, setQuantity] = useState(defaultQuantity);
  const { cartItems } = useClientFrontStoreCartItems();
  const { data: cart } = usePostParsedCartFromCartItems();
  const cartTotalPrice =
    (product.isFreeze ? cart?.freezeProductTotalPrice : cart?.normalProductTotalPrice) ?? 0;
  const { addToCart, addToCartDirect } = useAddToCart();
  const { isLoggedIn } = useAuth();
  const { subscriptionEnabled, setSubscriptionEnabled } = useSubscriptionStatus({
    canSubscribe: product.isOutlet ? false : product.subscription,
    cart,
  });
  const { redirectToSubscription } = useRedirectToSubscription({
    subscriptionEnabled,
    isFreezeProduct: product.isFreeze,
  });
  const pcExtraProps = !isMobile && { id: "getHeight" };
  const { addSetName } = useClientFrontStoreSetNames();

  // カート内に定期購入商品があるか
  const existSubscriptionInCart = useMemo(() => {
    return cartItems.some((item) => item.subscription && item.quantity > 0);
  }, [cartItems]);

  const {
    firstSubscriptionGapPrice,
    afterSecondSubscriptionGapPrice,
    firstSubscriptionSubtotal,
    afterSecondSubscriptionSubtotal,
    normalSubtotal,
    minimumPurchasePrice,
    productAndCartTotal,
    freeShippingPrice,
  } = usePrices({
    product,
    quantity,
    cartTotalPrice,
    subscriptionEnabled,
    cart,
  });

  const quantityOptions = useMemo(() => {
    return product.selectOptions.map((option) => ({
      label: option.toLocaleString(),
      value: option,
    }));
  }, [product.selectOptions]);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const validateAddingQuantity = useCallback(
    (addingQuantity: number) => {
      const currentQuantity = sumBy(
        cartItems.filter((item) => item.variantId === product.variantId),
        "quantity"
      );
      const totalQuantity = currentQuantity + addingQuantity;
      if (product.maxQuantity != null && product.maxQuantity < totalQuantity) {
        return `この商品は合計${product.maxQuantity}個までのご購入となります。`;
      }
    },
    [cartItems, product.maxQuantity, product.variantId]
  );

  // 1回のみ商品とアウトレットの商品の同時購入を不可とする
  const hasDifferentSalesTypeInCart = useMemo(
    () => cart?.products?.some((p) => product.salesType !== p.salesType) ?? false,
    [cart, product.salesType]
  );

  const showMyPageLink = isLoggedIn && (product.isSetProduct || subscriptionEnabled);

  const isDisabledSubmitDirect = useMemo(() => {
    // アウトレット商品については、最低購入金額のチェックを行わない
    return productAndCartTotal < minimumPurchasePrice && !showMyPageLink && !product.isOutlet;
  }, [productAndCartTotal, minimumPurchasePrice, showMyPageLink, product.isOutlet]);

  /**
   * スマートフォンの場合は、常に表示する必要があるため
   * toastとは別で管理する。
   */
  const [showSPNotification, setShowSpNotification] = useState(false);

  // カート追加
  const handleClickAddToCart = useCallback(async () => {
    const errorMessage = validateAddingQuantity(quantity);
    if (errorMessage) {
      setErrorMessage(errorMessage);
      return;
    }
    if (product.isSetProduct) {
      addSetName(product.setProductName || "");
      await addToCart(product.setProducts ?? [], subscriptionEnabled);
    } else {
      await addToCart([{ quantity, variantId: product.variantId }], subscriptionEnabled);
    }
    toast.success(
      <CartAddedNotification
        freeShippingPrice={freeShippingPrice}
        onLinkClick={() => toast.remove()}
      />,
      {
        // TODO: providerから取れるようにリファクタリング
        className: clsx(defaultToastClassNames.success, "clear__sp"),
      }
    );
    setShowSpNotification(true);

    // 現在アクティブな要素を取得する
    const activeElement = document.activeElement;
    // フォーカスを外す
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
  }, [
    addSetName,
    addToCart,
    freeShippingPrice,
    product.isSetProduct,
    product.setProductName,
    product.setProducts,
    product.variantId,
    quantity,
    subscriptionEnabled,
    validateAddingQuantity,
  ]);

  // いますぐ購入
  const handleClickDirect = useCallback(async () => {
    const errorMessage = validateAddingQuantity(quantity);
    if (errorMessage) {
      setErrorMessage(errorMessage);
      return;
    }
    if (product.isSetProduct) {
      addSetName(product.setProductName || "");
      await addToCartDirect(
        product.setProducts!,
        subscriptionEnabled,
        isDisabledSubmitDirect,
        product.setProductName
      );
    } else {
      await addToCartDirect(
        [{ quantity, variantId: product.variantId }],
        subscriptionEnabled,
        isDisabledSubmitDirect
      );
    }
  }, [
    addSetName,
    addToCartDirect,
    isDisabledSubmitDirect,
    product.isSetProduct,
    product.setProductName,
    product.setProducts,
    product.variantId,
    quantity,
    subscriptionEnabled,
    validateAddingQuantity,
  ]);

  // 商品をカスタマイズする
  const handleClickCustomize = useCallback(async () => {
    await addToCart(product.setProducts ?? [], subscriptionEnabled);
    if (product.isSetProduct) {
      addSetName(product.setProductName || "");
    }
    router.push("/cart");
  }, [
    addSetName,
    addToCart,
    product.isSetProduct,
    product.setProductName,
    product.setProducts,
    router,
    subscriptionEnabled,
  ]);

  return (
    <div>
      <div
        {...pcExtraProps}
        className={clsx(
          "mg__bottom__s",
          !isMobile && clsx(styles.hidden, !isScrollLimit && styles.on)
        )}
      >
        <div className="text__center">
          <p className={clsx("text__s", "text__bold", !isMobile && styles.newline)}>
            {`当サイトでのご購入が、\n送料含めお得です。`}
          </p>
        </div>
      </div>
      {product.isOutlet ? (
        <ProductDetailSelectsOutlet
          product={product}
          isMobile={isMobile}
          hasDifferentSalesTypeInCart={hasDifferentSalesTypeInCart}
          quantity={quantity}
          quantityOptions={quantityOptions}
          setQuantity={setQuantity}
          handleClickDirect={handleClickDirect}
          firstSubscriptionSubtotal={firstSubscriptionSubtotal}
          afterSecondSubscriptionSubtotal={afterSecondSubscriptionSubtotal}
          normalSubtotal={normalSubtotal}
          isDisabledSubmitDirect={isDisabledSubmitDirect}
        />
      ) : product.isSetProduct ? (
        <ProductDetailSelectsSet
          product={product}
          hasDifferentSalesTypeInCart={hasDifferentSalesTypeInCart}
          isLoggedIn={isLoggedIn}
          redirectToSubscription={redirectToSubscription}
          handleClickDirect={handleClickDirect}
          handleClickCustomize={handleClickCustomize}
        />
      ) : (
        <ProductDetailSelectsNormal
          product={product}
          isMobile={isMobile}
          isLoggedIn={isLoggedIn}
          subscriptionEnabled={subscriptionEnabled}
          hasDifferentSalesTypeInCart={hasDifferentSalesTypeInCart}
          isDisabledSubmitDirect={isDisabledSubmitDirect}
          setSubscriptionEnabled={setSubscriptionEnabled}
          firstSubscriptionGapPrice={firstSubscriptionGapPrice}
          afterSecondSubscriptionGapPrice={afterSecondSubscriptionGapPrice}
          quantity={quantity}
          quantityOptions={quantityOptions}
          setQuantity={setQuantity}
          normalSubtotal={normalSubtotal}
          firstSubscriptionSubtotal={firstSubscriptionSubtotal}
          afterSecondSubscriptionSubtotal={afterSecondSubscriptionSubtotal}
          redirectToSubscription={redirectToSubscription}
          handleClickDirect={handleClickDirect}
          handleClickAddToCart={handleClickAddToCart}
        />
      )}

      {isDisabledSubmitDirect && (
        <p className="text__s text__bold text__red mg__bottom__s mg__top__s text__center text__left__pc">
          {subscriptionEnabled || existSubscriptionInCart ? "継続コースの" : ""}
          合計金額
          {numberFormat(minimumPurchasePrice)}
          円以上からご購入いただけます。
        </p>
      )}

      {isMobile && (
        <MobileFloatPurchaseBars
          showCTAButton={showCTAButton}
          showSPNotification={showSPNotification}
          subscriptionEnabled={subscriptionEnabled}
          quantity={quantity}
          productVariantTitle={product.variantTitle}
          productMeal={product.meal}
          firstSubscriptionSubtotal={firstSubscriptionSubtotal}
          normalSubtotal={normalSubtotal}
          freeShippingPrice={freeShippingPrice}
          handleCloseSPNotification={() => setShowSpNotification(false)}
          cartAddLinkTitle={
            product.isOutlet ? "いますぐ購入" : showMyPageLink ? "マイページで追加" : "カートに追加"
          }
          onClickCartAddLink={
            product.isOutlet
              ? handleClickDirect
              : showMyPageLink
                ? redirectToSubscription
                : handleClickAddToCart
          }
          // マイページで追加・変更ボタンの場合は必ず活性化
          cartAddLinkDisabled={(product.isOutlet || !showMyPageLink) && hasDifferentSalesTypeInCart}
        />
      )}
      <Modal
        open={!!errorMessage}
        closeModal={() => setErrorMessage(null)}
        displayCloseButton={false}
        footer={
          <Button onClick={() => setErrorMessage(null)} variants="gray">
            閉じる
          </Button>
        }
      >
        <p className="text__red">{errorMessage}</p>
      </Modal>
    </div>
  );
});
